// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Operacional/CondicionalForm.tsx
//  Formulário de Venda Condicional + Ações de Venda/Devolução
// ════════════════════════════════════════════════════════════════════════════

import { ProdutoSearchCombobox } from '@/components/ProdutoSearchCombobox';
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Plus, Trash2, Save, Package, User, RotateCcw, CheckCircle2,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { condicionalService }    from '@/features/operacional/services/condicionalService';
import { PessoaSearchCombobox }  from '@/components/PessoaSearchCombobox';
import { VendedorSearchCombobox } from '@/components/VendedorSearchCombobox';
import { getApiErrorMessage }    from '@/lib/apiClient';
import { useAuth }               from '@/hooks/useAuth';

import { Button }                from '@/components/ui/button';
import { Input }                 from '@/components/ui/input';
import { Label }                 from '@/components/ui/label';
import { Textarea }              from '@/components/ui/textarea';
import { Badge }                 from '@/components/ui/badge';
import { ProdutoNome }           from '@/components/ProdutoNome';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Separator } from '@/components/ui/separator';

import type { CriarCondicionalRequest, CondicionalProdutoRequest } from '@/features/operacional/types';

// ── Item de produto ───────────────────────────────────────────────────────

interface ItemProduto {
  key: number;
  produtoId: number | '';
  termoBusca: string;
  qtdMovimento: number;
  valorUnitario: number;
  valorDesconto: number;
  valorAcrescimo: number;
  observacao: string;
}

const ITEM_VAZIO = (key: number): ItemProduto => ({
  key, produtoId: '', termoBusca: '',
  qtdMovimento: 1, valorUnitario: 0, valorDesconto: 0, valorAcrescimo: 0, observacao: '',
});

let _keySeq = 1;
const nextKey = () => ++_keySeq;

// ── Linha de produto ──────────────────────────────────────────────────────

function ProdutoLinha({ item, onChange, onRemove, showRemove, disabled }: {
  item: ItemProduto;
  onChange: (campo: keyof ItemProduto, valor: string | number) => void;
  onRemove: () => void;
  showRemove: boolean;
  disabled?: boolean;
}) {
  const subtotal = item.qtdMovimento * item.valorUnitario - item.valorDesconto + item.valorAcrescimo;

  return (
    <TableRow>
      <TableCell className="min-w-[220px]">
        <ProdutoSearchCombobox
          value={item.produtoId === '' ? undefined : item.produtoId}
          descricaoInicial={item.termoBusca || null}
          mostrarPreco
          disabled={disabled}
          placeholder="Buscar produto (código, nome ou EAN)..."
          onChange={() => {}}
          onSelectProduto={(p) => {
            onChange('produtoId', p.id);
            onChange('termoBusca', p.descricao);
            onChange('valorUnitario', p.precoPadrao ?? 0);
          }}
        />
      </TableCell>
      <TableCell className="w-20">
        <Input type="number" min={0.001} step={0.001} value={item.qtdMovimento} disabled={disabled}
          onChange={(e) => onChange('qtdMovimento', Number(e.target.value))} className="text-right" />
      </TableCell>
      <TableCell className="w-28">
        <Input type="number" min={0} step={0.01} value={item.valorUnitario} disabled={disabled}
          onChange={(e) => onChange('valorUnitario', Number(e.target.value))} className="text-right" />
      </TableCell>
      <TableCell className="w-24">
        <Input type="number" min={0} step={0.01} value={item.valorDesconto} disabled={disabled}
          onChange={(e) => onChange('valorDesconto', Number(e.target.value))} className="text-right" />
      </TableCell>
      <TableCell className="min-w-[160px]">
        <Input value={item.observacao} placeholder="Observação..." disabled={disabled} maxLength={300}
          onChange={(e) => onChange('observacao', e.target.value)} />
      </TableCell>
      <TableCell className="text-right font-medium w-28">
        {subtotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
      </TableCell>
      <TableCell className="w-10">
        {showRemove && !disabled && (
          <Button size="sm" variant="ghost" onClick={onRemove}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}

// ── Componente principal ──────────────────────────────────────────────────

const CondicionalForm = () => {
  const navigate  = useNavigate();
  const { id }    = useParams<{ id: string }>();
  const isEditing = !!id;
  const { user }  = useAuth();
  const queryClient = useQueryClient();

  const [vendedorId,    setVendedorId]    = useState<number | null>(null);
  const [clienteId,     setClienteId]     = useState<number | null>(null);
  const [nomeRazao,     setNomeRazao]     = useState('');
  const [cpfCnpj,       setCpfCnpj]       = useState('');
  const [fone,          setFone]          = useState('');
  const [celular,       setCelular]       = useState('');
  const [dtEmissao,     setDtEmissao]     = useState(() => new Date().toISOString().slice(0, 10));
  const [dtValidade,    setDtValidade]    = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 15); return d.toISOString().slice(0, 10);
  });
  const [observacao,    setObservacao]    = useState('');
  const [itens,         setItens]         = useState<ItemProduto[]>([ITEM_VAZIO(nextKey())]);
  // Acerto: quantidade a devolver AGORA por item (id do item → qtd).
  const [acertoQtd,     setAcertoQtd]     = useState<Record<number, number>>({});

  const { data: condicional } = useQuery({
    queryKey: ['condicional-detalhe', id],
    queryFn: () => condicionalService.getById(Number(id)),
    enabled: isEditing,
  });

  useEffect(() => {
    if (!condicional) return;
    setVendedorId(condicional.vendedorId);
    setClienteId(condicional.clienteId ?? null);
    setNomeRazao(condicional.nomeRazao ?? '');
    setCpfCnpj(condicional.cpfCnpj ?? '');
    setFone(condicional.fone ?? '');
    setCelular(condicional.celular ?? '');
    setDtEmissao(condicional.dtEmissao.slice(0, 10));
    setDtValidade(condicional.dtValidade.slice(0, 10));
    setObservacao(condicional.observacao ?? '');
  }, [condicional]);

  const totalBruto   = itens.reduce((s, i) => s + i.qtdMovimento * i.valorUnitario, 0);
  const totalDesconto = itens.reduce((s, i) => s + i.valorDesconto, 0);
  const totalLiquido = totalBruto - totalDesconto;

  const updateItem = (key: number, campo: keyof ItemProduto, valor: string | number) =>
    setItens((prev) => prev.map((it) => it.key === key ? { ...it, [campo]: valor } : it));
  const removeItem = (key: number) => setItens((prev) => prev.filter((it) => it.key !== key));

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!vendedorId) throw new Error('Vendedor obrigatório');
      const produtosValidos = itens.filter((it) => it.produtoId !== '');
      if (produtosValidos.length === 0) throw new Error('Adicione pelo menos um produto');
      const request: CriarCondicionalRequest = {
        vendedorId,
        dtEmissao: new Date(dtEmissao).toISOString(),
        dtValidade: new Date(dtValidade).toISOString(),
        clienteId: clienteId ?? undefined,
        nomeRazao: nomeRazao || undefined,
        cpfCnpj: cpfCnpj || undefined,
        fone: fone || undefined,
        celular: celular || undefined,
        observacao: observacao || undefined,
        produtos: produtosValidos.map<CondicionalProdutoRequest>((it) => ({
          produtoId: Number(it.produtoId),
          subProdutoId: 0,
          qtdMovimento: it.qtdMovimento,
          valorUnitario: it.valorUnitario,
          valorDesconto: it.valorDesconto,
          valorAcrescimo: it.valorAcrescimo,
          observacao: it.observacao || undefined,
        })),
      };
      return condicionalService.create(request);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['condicional'] });
      toast.success('Condicional salva com sucesso');
      navigate(`/operacional/condicional/${data.id}`);
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const acertoMutation = useMutation({
    mutationFn: (finalizarVenda: boolean) => {
      const itensAcerto = (condicional?.produtos ?? [])
        .filter((p) => !p.isDevolvido)
        .map((p) => ({
          produtoItemId: p.id,
          qtdDevolvida: p.qtdDevolvida + (acertoQtd[p.id] ?? 0),
        }));
      return condicionalService.acertar(Number(id), { itens: itensAcerto, finalizarVenda });
    },
    onSuccess: (_data, finalizarVenda) => {
      queryClient.invalidateQueries({ queryKey: ['condicional'] });
      queryClient.invalidateQueries({ queryKey: ['condicional-detalhe', id] });
      toast.success(finalizarVenda ? 'Acerto finalizado e venda confirmada' : 'Acerto registrado com sucesso');
      setAcertoQtd({});
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const venderMutation = useMutation({
    mutationFn: () => condicionalService.update(Number(id), { isBaixada: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['condicional'] });
      queryClient.invalidateQueries({ queryKey: ['condicional-detalhe', id] });
      toast.success('Venda confirmada com sucesso');
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const isBaixada   = condicional?.isBaixada ?? false;
  const isDevolvida = condicional?.isDevolvido ?? false;
  const bloqueado   = isBaixada || isDevolvida;

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/operacional/condicional')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">
              {isEditing ? `Condicional #${condicional?.numeroOrcamento ?? '...'}` : 'Nova Condicional'}
            </h1>
            {isBaixada && <Badge className="bg-green-500/10 text-green-500 mt-1">Vendida</Badge>}
            {isDevolvida && <Badge className="bg-gray-500/10 text-gray-500 mt-1">Devolvida</Badge>}
          </div>
        </div>
        <div className="flex gap-2">
          {isEditing && !bloqueado && (
            <Button variant="outline" className="gap-2 text-green-600 border-green-600 hover:bg-green-50"
              onClick={() => venderMutation.mutate()} disabled={venderMutation.isPending}>
              <CheckCircle2 className="h-4 w-4" />Confirmar Venda (todo o saldo)
            </Button>
          )}
          <Button variant="outline" onClick={() => navigate('/operacional/condicional')}>Cancelar</Button>
          {!bloqueado && (
            <Button className="gap-2" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
              <Save className="h-4 w-4" />Salvar
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="identificacao">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="identificacao" className="gap-2"><User className="h-4 w-4" />Identificação</TabsTrigger>
          <TabsTrigger value="produtos" className="gap-2"><Package className="h-4 w-4" />Produtos</TabsTrigger>
          <TabsTrigger value="observacoes" className="gap-2">Observações</TabsTrigger>
        </TabsList>

        {/* Identificação */}
        <TabsContent value="identificacao">
          <Card>
            <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Vendedor <span className="text-destructive">*</span></Label>
                <VendedorSearchCombobox value={vendedorId} onChange={setVendedorId}
                  placeholder="Buscar vendedor..." disabled={bloqueado} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Data de Emissão</Label>
                  <Input type="date" value={dtEmissao}
                    onChange={(e) => setDtEmissao(e.target.value)} disabled={bloqueado} />
                </div>
                <div className="space-y-2">
                  <Label>Validade</Label>
                  <Input type="date" value={dtValidade}
                    onChange={(e) => setDtValidade(e.target.value)} disabled={bloqueado} />
                </div>
              </div>
              <Separator className="md:col-span-2" />
              <div className="space-y-2">
                <Label>Cliente Cadastrado</Label>
                <PessoaSearchCombobox value={clienteId} onChange={setClienteId}
                  placeholder="Buscar cliente..." filter="cliente" disabled={bloqueado} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nome / Razão Social</Label>
                  <Input value={nomeRazao} onChange={(e) => setNomeRazao(e.target.value)}
                    placeholder="Nome do cliente" disabled={bloqueado} />
                </div>
                <div className="space-y-2">
                  <Label>CPF / CNPJ</Label>
                  <Input value={cpfCnpj} onChange={(e) => setCpfCnpj(e.target.value)}
                    placeholder="000.000.000-00" disabled={bloqueado} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Telefone</Label>
                <Input value={fone} onChange={(e) => setFone(e.target.value)}
                  placeholder="(00) 0000-0000" disabled={bloqueado} />
              </div>
              <div className="space-y-2">
                <Label>Celular</Label>
                <Input value={celular} onChange={(e) => setCelular(e.target.value)}
                  placeholder="(00) 00000-0000" disabled={bloqueado} />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Produtos */}
        <TabsContent value="produtos">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Produtos da Condicional</CardTitle>
                {!bloqueado && (
                  <Button size="sm" variant="outline" className="gap-2"
                    onClick={() => setItens((p) => [...p, ITEM_VAZIO(nextKey())])}>
                    <Plus className="h-4 w-4" />Adicionar item
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {isEditing && condicional ? (
                // Modo acerto: devolução parcial por quantidade + finalização da venda
                <>
                  {!bloqueado && (
                    <p className="text-sm text-muted-foreground mb-3">
                      Informe a quantidade a <strong>devolver agora</strong> de cada item. O que não for
                      devolvido permanece em poder do cliente até a próxima devolução ou a venda do saldo.
                    </p>
                  )}
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Produto</TableHead>
                          <TableHead className="text-right">Enviada</TableHead>
                          <TableHead className="text-right">Já devolv.</TableHead>
                          {!bloqueado && <TableHead className="text-right w-28">Devolver agora</TableHead>}
                          <TableHead className="text-right">Fica</TableHead>
                          <TableHead className="text-right">Vlr Unit.</TableHead>
                          <TableHead className="text-right">Subtotal (fica)</TableHead>
                          <TableHead>Observação</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {condicional.produtos.map((p) => {
                          const restante     = p.qtdMovimento - p.qtdDevolvida;
                          const devolverAgora = Math.min(acertoQtd[p.id] ?? 0, restante);
                          const fica         = restante - devolverAgora;
                          const totalmenteDevolvido = p.isDevolvido || restante <= 0;
                          return (
                            <TableRow key={p.id} className={totalmenteDevolvido ? 'opacity-60' : ''}>
                              <TableCell className="font-medium"><ProdutoNome id={p.produtoId} /></TableCell>
                              <TableCell className="text-right">{p.qtdMovimento}</TableCell>
                              <TableCell className="text-right">{p.qtdDevolvida || 0}</TableCell>
                              {!bloqueado && (
                                <TableCell className="text-right">
                                  <Input type="number" min={0} max={restante} step={1}
                                    className="text-right h-8"
                                    disabled={totalmenteDevolvido}
                                    value={acertoQtd[p.id] ?? 0}
                                    onChange={(e) => {
                                      const v = Math.max(0, Math.min(Number(e.target.value) || 0, restante));
                                      setAcertoQtd((prev) => ({ ...prev, [p.id]: v }));
                                    }} />
                                </TableCell>
                              )}
                              <TableCell className="text-right">{fica}</TableCell>
                              <TableCell className="text-right">
                                {p.valorUnitario.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                              </TableCell>
                              <TableCell className="text-right font-medium">
                                {(fica * p.valorUnitario).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                              </TableCell>
                              <TableCell className="text-muted-foreground text-sm">
                                {p.observacao || <span className="text-muted-foreground">—</span>}
                              </TableCell>
                              <TableCell>
                                {totalmenteDevolvido
                                  ? <Badge className="bg-gray-500/10 text-gray-500">Devolvido</Badge>
                                  : p.qtdDevolvida > 0
                                    ? <Badge className="bg-amber-500/10 text-amber-600">Parcial</Badge>
                                    : <Badge className="bg-blue-500/10 text-blue-500">Pendente</Badge>}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>

                  {!bloqueado && (
                    <div className="mt-4 flex flex-col sm:flex-row items-stretch sm:items-end justify-between gap-4">
                      <div className="w-full sm:w-72 space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">A devolver agora:</span>
                          <span>{condicional.produtos
                            .reduce((s, p) => s + Math.min(acertoQtd[p.id] ?? 0, p.qtdMovimento - p.qtdDevolvida) * p.valorUnitario, 0)
                            .toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
                        </div>
                        <Separator />
                        <div className="flex justify-between font-bold">
                          <span>Fica (a cobrar):</span>
                          <span>{condicional.produtos
                            .reduce((s, p) => s + Math.max(0, p.qtdMovimento - p.qtdDevolvida - (acertoQtd[p.id] ?? 0)) * p.valorUnitario, 0)
                            .toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 justify-end">
                        <Button variant="outline" className="gap-2"
                          onClick={() => acertoMutation.mutate(false)}
                          disabled={acertoMutation.isPending}>
                          <RotateCcw className="h-4 w-4" />Registrar Acerto
                        </Button>
                        <Button className="gap-2 bg-green-600 hover:bg-green-700"
                          onClick={() => acertoMutation.mutate(true)}
                          disabled={acertoMutation.isPending}>
                          <CheckCircle2 className="h-4 w-4" />Acertar e Vender o Saldo
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                // Modo criação
                <>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Produto</TableHead>
                          <TableHead className="text-right">Qtd</TableHead>
                          <TableHead className="text-right">Vlr Unit.</TableHead>
                          <TableHead className="text-right">Desconto</TableHead>
                          <TableHead>Observação</TableHead>
                          <TableHead className="text-right">Subtotal</TableHead>
                          <TableHead />
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {itens.map((item) => (
                          <ProdutoLinha key={item.key} item={item}
                            onChange={(campo, valor) => updateItem(item.key, campo, valor)}
                            onRemove={() => removeItem(item.key)}
                            showRemove={itens.length > 1} />
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="mt-4 flex justify-end">
                    <div className="w-64 space-y-1 text-sm">
                      <div className="flex justify-between"><span className="text-muted-foreground">Bruto:</span><span>{totalBruto.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span></div>
                      <div className="flex justify-between text-destructive"><span>Desconto:</span><span>- {totalDesconto.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span></div>
                      <Separator />
                      <div className="flex justify-between font-bold text-base"><span>Total:</span><span>{totalLiquido.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span></div>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Observações */}
        <TabsContent value="observacoes">
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-2">
                <Label>Observações</Label>
                <Textarea value={observacao} onChange={(e) => setObservacao(e.target.value)}
                  placeholder="Observações sobre a condicional..." rows={6} disabled={bloqueado} />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {!bloqueado && (
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => navigate('/operacional/condicional')}>Cancelar</Button>
          <Button className="gap-2" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
            <Save className="h-4 w-4" />Salvar
          </Button>
        </div>
      )}
    </div>
  );
};

export default CondicionalForm;
